/**
 * phaseLeadTime — can the equipment a phase needs actually arrive in time?
 *
 * WHY THIS EXISTS. {@link assessPhaseFeasibility} answers "is the mix big
 * enough" and {@link assessPhaseContingency} answers "does it survive losing
 * a block". Neither asks the question that most often decides a phase date:
 * long-lead generation equipment has to be ORDERED years before it runs, and
 * a phase COD can be arithmetically impossible on the day it is typed.
 *
 * The Redhead phase plan is again the worked case. Phase 2 targets 31 Dec
 * 2028 with a 400 MW gas-turbine entry. An H-class machine is 18 to 30
 * months from order, so holding that date needs a slot reserved by roughly
 * mid-2026 at the late end. Nothing in the platform said so, and the
 * commercial record shows no slot reserved.
 *
 * WHAT IT DOES NOT DO. It does not model construction, commissioning,
 * permitting or interconnection: it is an EQUIPMENT-DELIVERY floor, and the
 * real order-by date is earlier once those are layered on. It says so in the
 * statement rather than implying the floor is the schedule. Permitting in
 * particular routinely binds ahead of equipment.
 *
 * Lead times come from {@link LEAD_TIME_BASELINES}, the platform's cited
 * planning baseline, selected by phase size for gas turbines (an
 * aeroderivative tranche and an H-class block are not the same wait). A
 * component with no defensible baseline is skipped rather than guessed.
 *
 * Pure and side-effect free. `now` is injected so the check is deterministic
 * and testable; callers pass the report/generation timestamp.
 */
import type { PhasePowerComponentKey, ProjectPhase } from '../types/siteRag';
import { PHASE_POWER_COMPONENT_LABEL } from '../types/siteRag';
import { LEAD_TIME_BASELINES } from './supplyChainBaselines';

/** Months of margin below which a date is "tight" rather than impossible. */
const TIGHT_MARGIN_MONTHS = 6;

const MS_PER_MONTH = (365.25 / 12) * 24 * 60 * 60 * 1000;

/**
 * Baseline key per phase component. Gas turbines resolve by SIZE because the
 * class drives the wait; `recip` has no generation baseline of its own and
 * maps to the aeroderivative package as the closest cited proxy, which the
 * output labels rather than hides.
 */
function baselineKeyFor(
  component: PhasePowerComponentKey,
  capacityMW: number,
): { key: string; proxy: boolean } | null {
  switch (component) {
    case 'gas-turbine':
      if (capacityMW >= 300) return { key: 'gas-turbine-h-class', proxy: false };
      if (capacityMW >= 100) return { key: 'gas-turbine-f-class', proxy: false };
      return { key: 'gas-turbine-aeroderivative', proxy: false };
    case 'recip':
      // No reciprocating-engine baseline exists; the aeroderivative package is
      // the nearest cited class (similar scale, similar shop build).
      return { key: 'gas-turbine-aeroderivative', proxy: true };
    case 'smr':
    case 'large-nuclear':
      return { key: 'smr-module-nuscale-class', proxy: component === 'large-nuclear' };
    case 'solar-bess':
      return { key: 'pv-module-bifacial', proxy: false };
    case 'wind-bess':
      return { key: 'wind-turbine-onshore-class-ii', proxy: false };
    case 'battery':
      return { key: 'bess-lfp-container-4hr', proxy: false };
    case 'grid':
      // Grid supply is an interconnection QUEUE, not an equipment order. It has
      // its own study/upgrade track and is deliberately out of scope here.
      return null;
    default:
      return null;
  }
}

export type PhaseLeadTimeVerdict = 'ok' | 'tight' | 'infeasible' | 'indeterminate';

export interface PhaseLeadTimeComponent {
  component: PhasePowerComponentKey;
  capacityMW: number;
  baselineKey: string;
  baselineName: string;
  leadTimeMonthsLow: number;
  leadTimeMonthsHigh: number;
  /** True when the baseline is a nearest-class stand-in, not an exact match. */
  proxyBaseline: boolean;
  /** Latest order date that still holds the COD, at the HIGH (safe) lead time. */
  orderByIso: string | null;
  /** Months from `now` to that date; negative means the window has passed. */
  monthsOfMargin: number | null;
  verdict: PhaseLeadTimeVerdict;
}

export interface PhaseLeadTimeAssessment {
  phaseId: string;
  phaseLabel: string;
  phaseSequence: number;
  targetInServiceDate: string | null;
  components: PhaseLeadTimeComponent[];
  /** The component with the least margin: what actually binds the phase. */
  binding: PhaseLeadTimeComponent | null;
  verdict: PhaseLeadTimeVerdict;
  statement: string;
}

const round1 = (n: number): number => Math.round(n * 10) / 10;

/**
 * Subtract whole months, CLAMPING the day to the target month's length.
 * Naive `setMonth` overflows instead: 31 Dec minus 30 months lands on 1 Jul
 * rather than 30 Jun, which would quote an order-by date a day later than
 * the arithmetic allows.
 */
function isoMonthsBefore(iso: string, months: number): string {
  const src = new Date(Date.parse(iso));
  const day = src.getUTCDate();
  const target = new Date(Date.UTC(src.getUTCFullYear(), src.getUTCMonth() - Math.round(months), 1));
  const daysInTargetMonth = new Date(Date.UTC(
    target.getUTCFullYear(), target.getUTCMonth() + 1, 0,
  )).getUTCDate();
  target.setUTCDate(Math.min(day, daysInTargetMonth));
  return target.toISOString().slice(0, 10);
}

/**
 * Assess whether a phase's generation equipment can be delivered in time for
 * its declared COD. Returns `null` when the phase carries no dated COD or no
 * component with a defensible lead-time baseline: an unknown is reported as
 * absent, never as a pass.
 */
export function assessPhaseLeadTime(
  phase: ProjectPhase,
  now: Date,
): PhaseLeadTimeAssessment | null {
  const cod = phase.targetInServiceDate;
  const codMs = cod ? Date.parse(cod) : Number.NaN;
  const params = phase.powerSourceConfig?.componentParams ?? {};

  const components: PhaseLeadTimeComponent[] = [];
  for (const [rawKey, value] of Object.entries(params)) {
    const component = rawKey as PhasePowerComponentKey;
    const capacityMW = typeof value?.capacityMW === 'number' && Number.isFinite(value.capacityMW)
      ? value.capacityMW
      : 0;
    if (capacityMW <= 0) continue;
    const resolved = baselineKeyFor(component, capacityMW);
    if (!resolved) continue;
    const baseline = LEAD_TIME_BASELINES.find((entry) => entry.key === resolved.key);
    if (!baseline) continue;

    const hasCod = Number.isFinite(codMs);
    const orderByIso = hasCod ? isoMonthsBefore(cod!, baseline.leadTimeMonthsHigh) : null;
    const monthsOfMargin = hasCod
      ? round1((Date.parse(orderByIso!) - now.getTime()) / MS_PER_MONTH)
      : null;
    const verdict: PhaseLeadTimeVerdict = monthsOfMargin == null
      ? 'indeterminate'
      : monthsOfMargin < 0
        ? 'infeasible'
        : monthsOfMargin < TIGHT_MARGIN_MONTHS
          ? 'tight'
          : 'ok';

    components.push({
      component,
      capacityMW,
      baselineKey: baseline.key,
      baselineName: baseline.name,
      leadTimeMonthsLow: baseline.leadTimeMonthsLow,
      leadTimeMonthsHigh: baseline.leadTimeMonthsHigh,
      proxyBaseline: resolved.proxy,
      orderByIso,
      monthsOfMargin,
      verdict,
    });
  }
  if (components.length === 0) return null;

  const RANK: Record<PhaseLeadTimeVerdict, number> = {
    infeasible: 3, tight: 2, indeterminate: 1, ok: 0,
  };
  const binding = components.reduce<PhaseLeadTimeComponent | null>((worst, curr) => {
    if (worst == null) return curr;
    if (RANK[curr.verdict] !== RANK[worst.verdict]) {
      return RANK[curr.verdict] > RANK[worst.verdict] ? curr : worst;
    }
    if (curr.monthsOfMargin == null || worst.monthsOfMargin == null) return worst;
    return curr.monthsOfMargin < worst.monthsOfMargin ? curr : worst;
  }, null);
  const verdict = binding?.verdict ?? 'indeterminate';

  const label = binding ? PHASE_POWER_COMPONENT_LABEL[binding.component] : null;
  let head: string;
  if (!binding || verdict === 'indeterminate') {
    head = `Phase ${phase.sequence} carries long-lead generation equipment but no dated COD, so delivery cannot be checked.`;
  } else {
    const window = `${binding.leadTimeMonthsLow}-${binding.leadTimeMonthsHigh} months`;
    const orderBy = `order by ${binding.orderByIso}`;
    head = `${label} (${round1(binding.capacityMW)} MW) is ${window} from order on the `
      + `${binding.baselineName} baseline, so holding ${cod} means ${orderBy}`;
    head += verdict === 'infeasible'
      ? `: that window has already passed by ${Math.abs(binding.monthsOfMargin ?? 0)} months.`
      : verdict === 'tight'
        ? `: only ${binding.monthsOfMargin} months from now.`
        : `, ${binding.monthsOfMargin} months from now.`;
  }
  const proxyNote = components.some((c) => c.proxyBaseline)
    ? ' One or more components use a nearest-class lead-time proxy; confirm with the OEM.'
    : '';
  const scopeNote = ' Equipment delivery only: construction, commissioning, permitting and '
    + 'interconnection are additional, so the real order-by date is earlier.';

  return {
    phaseId: phase.id,
    phaseLabel: phase.label,
    phaseSequence: phase.sequence,
    targetInServiceDate: cod,
    components,
    binding,
    verdict,
    statement: `${head}${proxyNote}${scopeNote}`,
  };
}
